import { GeoCoords, GeoMeasurement, GeoPointInput, SavedGeo } from './geoApi';

/**
 * Чистые расчеты мастера местоположения. Границы совпадают с сервером (backend/services/geoPoint.js):
 * сервер проверяет их повторно и сам считает точность пина, здесь - только подсказки на экране.
 */
export const MAX_ACCURACY = 30;
export const MIN_PIN_ZOOM = 17;
export const MAX_PIN_ZOOM = 21;
/** С этого масштаба видны отдельные входы в здание */
export const SHARP_PIN_ZOOM = 19;
/** Пин дальше этого от замера - повод перепроверить, что выбрано свое здание */
export const FAR_SHIFT_M = 300;
/** Короче сервер не ищет */
export const MIN_SEARCH = 3;
export const DEFAULT_CENTER: GeoCoords = { latitude: 42.3417, longitude: 69.5901 };
export const DEFAULT_ZOOM = 13;

const PIN_MISS_PX = 20;
const EQUATOR_M_PER_PX = 156543.03;
const EARTH_RADIUS_M = 6371000;
// Карта, поставленная ровно на замер, может сместить центр на долю экранной точки
const UNMOVED_M = 1.5;

/** Погрешность пина, м: промах в 20 экранных точек на этой широте и масштабе */
export function pinAccuracy(latitude: number, zoom: number): number {
  const metersPerPx = EQUATOR_M_PER_PX * Math.cos(latitude * Math.PI / 180) / Math.pow(2, zoom);
  return Math.max(1, Math.round(metersPerPx * PIN_MISS_PX));
}

export function distanceMeters(a: GeoCoords, b: GeoCoords): number {
  const rad = Math.PI / 180;
  const dLat = (b.latitude - a.latitude) * rad;
  const dLng = (b.longitude - a.longitude) * rad;
  const h = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(a.latitude * rad) * Math.cos(b.latitude * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export type MeasureQuality = 'precise' | 'good' | 'rough' | 'poor';

/** Оценка замера для индикатора: до 30 м замер сохраняется как есть, хуже - пин нужно поставить вручную */
export function measureQuality(accuracy: number): { quality: MeasureQuality; label: string; color: string } {
  if (accuracy <= 12) return { quality: 'precise', label: 'Отличная точность', color: '#16a34a' };
  if (accuracy <= MAX_ACCURACY) return { quality: 'good', label: 'Хорошая точность', color: '#16a34a' };
  if (accuracy <= 150) return { quality: 'rough', label: 'Примерно: уточните на карте', color: '#d97706' };
  return { quality: 'poor', label: 'Только район: найдите здание на карте', color: '#dc2626' };
}

/** Масштаб карты под радиус замера: чем точнее замер, тем ближе */
function zoomForAccuracy(accuracy: number): number {
  if (accuracy <= MAX_ACCURACY) return 18;
  if (accuracy <= 150) return 17;
  if (accuracy <= 1000) return 15;
  return DEFAULT_ZOOM;
}

/** С чего начать карту: замер, если он не хуже сохраненной точки; иначе сохраненная точка; иначе центр города */
export function startView(measured: GeoMeasurement | null, saved: SavedGeo | null): GeoCoords & { zoom: number } {
  const savedAccuracy = saved && saved.accuracy != null ? saved.accuracy : MAX_ACCURACY;
  if (measured && (!saved || measured.accuracy <= Math.max(savedAccuracy, 150))) {
    return { latitude: measured.latitude, longitude: measured.longitude, zoom: zoomForAccuracy(measured.accuracy) };
  }
  if (saved) return { latitude: saved.latitude, longitude: saved.longitude, zoom: 18 };
  return { ...DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
}

/**
 * Точка для сохранения: пин не сдвинут с точного замера - сохраняется замер (gps), иначе - пин (manual).
 * null - сохранить нельзя: пин поставлен на слишком мелком масштабе
 */
export function pointToSave(pin: GeoCoords, zoom: number, measured: GeoMeasurement | null): GeoPointInput | null {
  if (measured && measured.accuracy <= MAX_ACCURACY && distanceMeters(pin, measured) < UNMOVED_M) {
    return { source: 'gps', latitude: measured.latitude, longitude: measured.longitude, accuracy: measured.accuracy };
  }
  if (zoom < MIN_PIN_ZOOM) return null;
  return { source: 'manual', latitude: pin.latitude, longitude: pin.longitude, zoom: Math.min(MAX_PIN_ZOOM, Math.round(zoom)), measured };
}

/** Точность, которая будет сохранена для точки */
export function savedAccuracy(point: GeoPointInput): number {
  return point.source === 'gps' ? Math.round(point.accuracy) : pinAccuracy(point.latitude, point.zoom);
}

export function formatCoords(point: GeoCoords): string {
  return `${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}`;
}

export function formatDistance(meters: number): string {
  if (meters < 1) return 'меньше 1 м';
  if (meters < 1000) return `${Math.round(meters)} м`;
  return `${(meters / 1000).toFixed(1).replace('.', ',')} км`;
}

/** Обратный отсчет до срока ссылки: «9:05»; 0 - срок вышел */
export function formatCountdown(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const seconds = total % 60;
  return `${Math.floor(total / 60)}:${seconds < 10 ? '0' : ''}${seconds}`;
}
